import { types } from "mobx-state-tree";
import jsYaml from "js-yaml";

export const Mk8sDraftAddonByokModel = types
  .model({
    _isEnabled: types.optional(types.boolean, false),
    isEnabled: types.optional(types.boolean, false),
    _location: types.optional(types.string, ""),
    location: types.optional(types.string, ""),
    _ignoreUpdates: types.optional(types.boolean, false),
    ignoreUpdates: types.optional(types.boolean, false),
    _config: types.optional(types.string, () => jsYaml.dump(getDefaultByokConfig())),
    config: types.optional(types.string, ""),
  })
  .actions((self) => ({
    setIsEnabled(value: boolean) {
      self.isEnabled = value;
    },
    setIgnoreUpdates(value: boolean) {
      self.ignoreUpdates = value;
    },
    setLocation(value: string) {
      self.location = value;
    },
    setConfig(value: string) {
      self.config = value;
    },
  }))
  .actions((self) => ({
    reset() {
      self.isEnabled = self._isEnabled;
      self.location = self._location;
      self.ignoreUpdates = self._ignoreUpdates;
      self.config = self._config;
    },
    confirm() {
      self._isEnabled = self.isEnabled;
      self._location = self.location;
      self._ignoreUpdates = self.ignoreUpdates;
      self._config = self.config;
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.reset();
    },
  }))
  .views((self) => ({
    get asObject() {
      try {
        if (self.config.trim().length < 1) {
          return {
            location: `//location/${self.location}`,
            ignoreUpdates: self.ignoreUpdates,
          };
        }
        const configObject = jsYaml.load(self.config) as any;
        if (Object.keys(configObject).includes("0")) {
          return false;
        }
        let res: any = {
          location: `//location/${self.location}`,
          ignoreUpdates: self.ignoreUpdates,
          config: configObject,
        };
        return res;
      } catch (e) {
        return false;
      }
    },
  }))
  .views((self) => ({
    get isValid() {
      let res = true;
      if (self.isEnabled) {
        if (!self.location) res = false;
      }
      if (self.asObject === false) {
        return false;
      }
      return res;
    },
    get invalidReason() {
      let reason = "";
      if (self.isEnabled) {
        if (!self.location) reason = "no location";
      }
      if (self.asObject === false) {
        return "cant parse config";
      }
      return reason;
    },
    get isDirty() {
      let res = false;
      if (self.isEnabled !== self._isEnabled) res = true;
      if (self.isEnabled) {
        if (self.location !== self._location) res = true;
        if (self.ignoreUpdates !== self._ignoreUpdates) res = true;
        if (self.config !== self._config) res = true;
      }
      return res;
    },
    get dirtyReason() {
      let reason = "";
      if (self.isEnabled !== self._isEnabled) reason = "enabled changed";
      if (self.isEnabled) {
        if (self.location !== self._location) reason = "location changed";
        if (self.ignoreUpdates !== self._ignoreUpdates) reason = "ignoreUpdates changed";
        if (self.config !== self._config) reason = "config changed";
      }
      return reason;
    },
  }));

export function getDefaultByokConfig() {
  return {
    actuator: {
      minCpu: "50m",
      maxCpu: "8001m",
      minMemory: "200Mi",
      maxMemory: "8000Mi",
      logLevel: "info",
      env: {
        CACHE_PERIOD_DATA_SERVICE: "600",
        LABEL_NODES: "false",
      },
    },
    middlebox: {
      enabled: false,
      bandwidthAlertMbps: 650,
    },
    common: {
      deploymentReplicas: 1,
      pdb: {
        maxUnavailable: 1,
      },
    },
    byok: {
      noDefaultStorageClasses: false,
    },
    longhorn: {
      numberOfReplicas: 2,
    },
    ingress: {
      cpu: "50m",
      memory: "200Mi",
      targetPercent: 6000,
    },
    istio: {
      istiod: {
        replicas: 2,
        minCpu: "50m",
        maxCpu: "8001m",
        minMemory: "100Mi",
        maxMemory: "8000Mi",
        pdb: 0,
      },
      ingressgateway: {
        replicas: 2,
        maxCpu: "1",
        maxMemory: "1Gi",
      },
      sidecar: {
        minCpu: "0m",
        minMemory: "200Mi",
      },
    },
    logSplitter: {
      minCpu: "1m",
      maxCpu: "1000m",
      minMemory: "10Mi",
      maxMemory: "2000Mi",
      memBufferSize: "128M",
      perPodRate: 10000,
    },
    monitoring: {
      minMemory: "100Mi",
      maxMemory: "20Gi",
      kubeStateMetrics: {
        minMemory: "25Mi",
      },
      prometheus: {
        main: {
          storage: "10Gi",
        },
      },
      remoteWrite: [],
      externalLabels: {},
    },
    redis: {
      minCpu: "10m",
      maxCpu: "2001m",
      minMemory: "100Mi",
      maxMemory: "1000Mi",
      storage: "8Gi",
    },
    redisHa: {
      minCpu: "50m",
      maxCpu: "2001m",
      minMemory: "100Mi",
      maxMemory: "1000Mi",
      storage: 0,
    },
    redisSentinel: {
      minCpu: "10m",
      maxCpu: "500m",
      minMemory: "10Mi",
      maxMemory: "400Mi",
      storage: 0,
    },
    tempoAgent: {
      minCpu: "0m",
      minMemory: "10Mi",
    },
  };
}
